"use client";

import { useMemo, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { SmrSummary } from "@/components/tools/stats-clinical/smr-results";
import { PasteBox } from "@/components/tools/stats-research/paste-box";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import {
  type SmrStratum,
  smrFromCounts,
  standardizedMortalityRatio,
} from "@/lib/tool-kit/stats-clinical/smr";
import {
  type HeaderMode,
  parseNumericTable,
} from "@/lib/tool-kit/stats-research/matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const STANDARDIZED_MORTALITY_RATIO_CALCULATOR_STRINGS = {
  modeLabel: "Start from",
  modeStrata: "Age groups (strata) and reference rates",
  modeCounts: "Observed and expected deaths",
  dataLabel:
    "One row per age group: observed deaths, person-years, reference rate (a group name first is optional)",
  dataHint:
    "Paste from a spreadsheet, or separate the values with tabs, commas or spaces. The reference rate is the death rate of the comparison population in that age group, per the rate base chosen below. The example uses made-up numbers.",
  dataPlaceholder: "40–44\t3\t3800\t110\n45–49\t7\t5200\t190",
  dataExample:
    "Age group\tObserved deaths\tPerson-years\tReference rate\n40–44\t3\t3800\t110\n45–49\t7\t5200\t190\n50–54\t14\t6100\t330\n55–59\t19\t4700\t560\n60–64\t17\t2900\t900",
  loadExample: "Load example",
  clear: "Clear",
  headerLabel: "Header row",
  headerAuto: "Detect automatically",
  headerYes: "First row is a header",
  headerNo: "No header row",
  rateBaseLabel: "Reference rates are per",
  rateBase1: "1 person-year",
  rateBase1000: "1,000 person-years",
  rateBase10000: "10,000 person-years",
  rateBase100000: "100,000 person-years",
  rateBase1000000: "1,000,000 person-years",
  levelLabel: "Confidence level",
  observedLabel: "Observed deaths (O)",
  expectedLabel: "Expected deaths (E)",
  observedPlaceholder: "60",
  expectedPlaceholder: "86.61",
  stratumName: "Group {number}",
  unreadable: "Line {line}, column {column}: “{text}” is not a number.",
  needColumns:
    "Each row needs three numbers: observed deaths, person-years and the reference rate.",
  badRow:
    "Line {line}: observed deaths must be a whole number, and person-years and the rate cannot be negative.",
  nothingExpected:
    "The expected number of deaths is zero, so the ratio is undefined. Check the person-years and the reference rates.",
  countsInvalid:
    "Observed deaths must be a whole number (zero or more) and expected deaths must be above zero.",
  enterPrompt:
    "Enter age-specific deaths, person-years and reference rates, or the observed and expected deaths, to get the SMR.",
  results: "Results",
  cardSmr: "Standardized mortality ratio",
  cardInterval: "{level}% CI (exact)",
  cardP: "p (SMR = 1)",
  verdictLower:
    "SMR = {smr} ({times100} when multiplied by 100): {observed} deaths were observed where {expected} were expected, and the interval lies below 1 — fewer deaths than the reference rates would predict.",
  verdictHigher:
    "SMR = {smr} ({times100} when multiplied by 100): {observed} deaths were observed where {expected} were expected, and the interval lies above 1 — more deaths than the reference rates would predict.",
  verdictUncertain:
    "SMR = {smr} ({times100} when multiplied by 100): {observed} deaths were observed where {expected} were expected, but the interval includes 1, so the data are compatible with the reference rates.",
  columnMeasure: "Measure",
  columnValue: "Value",
  rowObserved: "Observed deaths (O)",
  rowExpected: "Expected deaths (E)",
  rowSmr: "SMR = O / E",
  rowSmr100: "SMR × 100",
  rowInterval: "{level}% exact Poisson CI",
  rowExcess: "Excess deaths (O − E)",
  strataHeading: "By age group",
  columnStratum: "Group",
  columnObserved: "Observed",
  columnPersonYears: "Person-years",
  columnRate: "Reference rate",
  columnExpected: "Expected",
  columnRatio: "O / E",
  total: "Total",
  howCalculated: "How this was calculated",
  workingExpected:
    "Expected deaths E = Σ (reference rate × person-years ÷ rate base), summed over the age groups.",
  workingSmr: "SMR = O / E = {observed} / {expected} = {smr}",
  workingNote:
    "The interval is the exact (Garwood) Poisson interval for the observed count, divided by E: χ² quantiles with 2O and 2O + 2 degrees of freedom. The p-value is the exact two-sided Poisson test of O against E.",
  report:
    "SMR = {smr}, {level}% CI [{lower}, {upper}], {observed} observed and {expected} expected deaths, p {p}.",
  copyResult: "Copy result",
  copied: "Copied!",
} as const;

type Mode = "strata" | "counts";

const RATE_BASES = [
  { value: "1", key: "rateBase1" },
  { value: "1000", key: "rateBase1000" },
  { value: "10000", key: "rateBase10000" },
  { value: "100000", key: "rateBase100000" },
  { value: "1000000", key: "rateBase1000000" },
] as const;

const LEVELS = ["0.9", "0.95", "0.99"] as const;

type Parsed =
  | { ok: true; strata: SmrStratum[] }
  | { ok: false; problem: string | null };

function readStrata(
  table: ReturnType<typeof parseNumericTable>,
  strings: typeof STANDARDIZED_MORTALITY_RATIO_CALCULATOR_STRINGS,
  fmt: (value: number) => string
): Parsed {
  const [issue] = table.unreadable;
  if (issue) {
    return {
      ok: false,
      problem: t(strings.unreadable, {
        line: fmt(issue.line),
        column: fmt(issue.column),
        text: issue.text,
      }),
    };
  }
  if (table.rows.length === 0) {
    return { ok: false, problem: null };
  }
  const strata: SmrStratum[] = [];
  for (const [index, row] of table.rows.entries()) {
    const [observed, personYears, rate] = row;
    if (observed == null || personYears == null || rate == null) {
      return { ok: false, problem: strings.needColumns };
    }
    if (
      !Number.isInteger(observed) ||
      observed < 0 ||
      personYears < 0 ||
      rate < 0
    ) {
      return {
        ok: false,
        problem: t(strings.badRow, { line: fmt(table.lines[index]) }),
      };
    }
    strata.push({
      label:
        table.rowLabels?.[index] ||
        t(strings.stratumName, { number: fmt(index + 1) }),
      observed,
      personYears,
      rate,
    });
  }
  return { ok: true, strata };
}

export default function StandardizedMortalityRatioCalculatorWidget() {
  const strings = useToolUi(STANDARDIZED_MORTALITY_RATIO_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("strata");
  const [text, setText] = useState("");
  const [header, setHeader] = useState<HeaderMode>("auto");
  const [rateBase, setRateBase] = useState("100000");
  const [level, setLevel] = useState("0.95");
  const [observedText, setObservedText] = useState("");
  const [expectedText, setExpectedText] = useState("");

  const table = useMemo(() => parseNumericTable(text, header), [text, header]);
  const parsed = readStrata(table, strings, (value) => fmt(value));
  const strataResult = parsed.ok
    ? standardizedMortalityRatio(parsed.strata, Number(rateBase), Number(level))
    : null;
  const observed = parseIntegerInput(observedText);
  const expected = parseNumberInput(expectedText);
  const countsResult =
    observed !== null && expected !== null
      ? smrFromCounts(observed, expected, Number(level))
      : null;
  const result =
    mode === "strata"
      ? strataResult
      : countsResult && { ...countsResult, strata: [] };

  let problem: string | null = null;
  if (mode === "strata") {
    if (!parsed.ok) {
      problem = parsed.problem;
    } else if (!strataResult) {
      problem = strings.nothingExpected;
    }
  } else if (
    observedText.trim() !== "" &&
    expectedText.trim() !== "" &&
    !countsResult
  ) {
    problem = strings.countsInvalid;
  }
  const entered =
    mode === "strata"
      ? text.trim() !== ""
      : observedText.trim() !== "" || expectedText.trim() !== "";

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="smr-mode"
        onChange={setMode}
        options={[
          { value: "strata", label: strings.modeStrata },
          { value: "counts", label: strings.modeCounts },
        ]}
        value={mode}
      />
      {mode === "strata" ? (
        <>
          <PasteBox
            actions={[
              {
                label: strings.loadExample,
                onClick: () => {
                  setHeader("auto");
                  setText(strings.dataExample);
                  setRateBase("100000");
                },
              },
              { label: strings.clear, onClick: () => setText("") },
            ]}
            header={{
              label: strings.headerLabel,
              value: header,
              onChange: setHeader,
              options: [
                { value: "auto", label: strings.headerAuto },
                { value: "yes", label: strings.headerYes },
                { value: "no", label: strings.headerNo },
              ],
            }}
            hint={strings.dataHint}
            id="smr-data"
            label={strings.dataLabel}
            onChange={setText}
            placeholder={strings.dataPlaceholder}
            rows={7}
            value={text}
          />
          <SelectField
            id="smr-base"
            label={strings.rateBaseLabel}
            onChange={setRateBase}
            options={RATE_BASES.map(({ value, key }) => ({
              value,
              label: strings[key],
            }))}
            value={rateBase}
          />
        </>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="smr-observed"
            label={strings.observedLabel}
            numeric
            onChange={setObservedText}
            placeholder={strings.observedPlaceholder}
            value={observedText}
          />
          <TextField
            id="smr-expected"
            label={strings.expectedLabel}
            numeric
            onChange={setExpectedText}
            placeholder={strings.expectedPlaceholder}
            value={expectedText}
          />
        </div>
      )}
      <div className="max-w-xs">
        <SelectField
          id="smr-level"
          label={strings.levelLabel}
          onChange={setLevel}
          options={LEVELS.map((value) => ({
            value,
            label: `${fmt(Number(value) * 100)}%`,
          }))}
          value={level}
        />
      </div>
      {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
      {result && !problem ? (
        <SmrSummary fmt={fmt} result={result} strings={strings} />
      ) : null}
      {entered ? null : <Notice>{strings.enterPrompt}</Notice>}
    </div>
  );
}
